import { useMemo } from 'react'
import { Outlet } from 'react-router-dom'

import { WorkScheduleRootPageDocument } from 'generated/resources/graphql'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import type { Back } from 'modules/core/components/Card/types'
import Loading from 'modules/core/components/Loading'
import NotFound from 'modules/core/components/NotFound'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import type { Weekdays } from 'modules/core/lib/firstDayOfWeek'
import i18n from 'modules/core/lib/i18n'
import DayConfiguration from 'modules/work_schedules/store/models/DayConfiguration'
import OverlapPeriod, { type ScheduleType } from 'modules/work_schedules/store/models/OverlapPeriod'
import Schedule from 'modules/work_schedules/store/models/Schedule'

const toScheduleType = (value: string): ScheduleType => {
  if (value === 'fixed') {
    return 'fixed'
  }

  return 'flexible'
}

const toWeekday = (value: string): Weekdays => {
  if (value === 'friday') {
    return 'friday'
  }

  if (value === 'monday') {
    return 'monday'
  }

  if (value === 'saturday') {
    return 'saturday'
  }

  if (value === 'sunday') {
    return 'sunday'
  }

  if (value === 'thursday') {
    return 'thursday'
  }

  if (value === 'tuesday') {
    return 'tuesday'
  }

  if (value === 'wednesday') {
    return 'wednesday'
  }

  return 'monday'
}
const WorkScheduleRootPage = () => {
  const { navigation } = useNavigation()
  const { id } = useStrictParams<'id'>()

  const { data, loading: loadingGraphql } = useQuery(WorkScheduleRootPageDocument, {
    variables: {
      scheduleId: String(id),
    },
  })

  const back: Back = {
    href: navigation.settings.workSchedule.fullPath(),
    label: i18n.t('settings.time_management.tab'),
  }

  const scheduleNode = data?.workSchedule.schedule
  const workSchedule = useMemo(() => {
    if (!scheduleNode) {
      return undefined
    }

    return new Schedule({
      id: String(scheduleNode.id),
      name: scheduleNode.name,
      company_id: '',
      archived_at: scheduleNode.archivedAt ?? null,
      created_at: '',
      updated_at: '',
      periods: [],
      employees: scheduleNode.employeeIds.map((employeeId) => String(employeeId)),
    })
  }, [scheduleNode])

  const overlapPeriods = useMemo(() => {
    if (!scheduleNode) {
      return []
    }

    return scheduleNode.overlapPeriods.map(
      (period) =>
        new OverlapPeriod({
          id: String(period.id),
          default: period.default,
          schedule_id: String(scheduleNode.id),
          start_date: period.startDate,
          end_date: period.endDate,
          schedule_type: toScheduleType(period.scheduleType),
        })
    )
  }, [scheduleNode])

  const dayConfigurations = useMemo(() => {
    if (!scheduleNode) {
      return []
    }

    return scheduleNode.overlapPeriods.flatMap((period) =>
      period.dayConfigurations.map(
        (configuration) =>
          new DayConfiguration({
            id: String(configuration.id),
            weekday: toWeekday(configuration.weekday),
            start_at: configuration.formattedStartAt ?? '',
            end_at: configuration.formattedEndAt ?? '',
            duration_in_seconds: configuration.durationInSeconds,
            overlap_period_id: String(period.id),
          })
      )
    )
  }, [scheduleNode])

  const workScheduleId = workSchedule?.attributes.id
  const defaultPeriod = workScheduleId
    ? overlapPeriods.find(
        (period) => period.attributes.default && period.attributes.schedule_id === workScheduleId
      )
    : undefined

  if (loadingGraphql) return <Loading />
  if (!workSchedule) return <NotFound />

  return (
    <AuthorizeUser
      authorizeFn={() => data?.workSchedule?._policies?.manageCompanySchedules?.can ?? false}
    >
      <Outlet
        context={{
          isNotFound: !workSchedule.id,
          back,
          workSchedule,
          overlapPeriods,
          defaultPeriod,
          dayConfigurations,
          loading: loadingGraphql,
        }}
      />
    </AuthorizeUser>
  )
}

export default WorkScheduleRootPage
